import { useState, useEffect } from "react";
import axios from "axios";
import { Apiurl } from "../../../shared/services/apirest";
import { getAuthHeader } from "../utils/investigadorUtils";

export const useRegistrarCaso = (selectedPaciente, initialData, isEditMode, activeProjectId, showToast, setSelectedPaciente, onCancel, setView) => {
    const effectivePaciente = isEditMode && initialData ? {
        id: initialData.paciente,
        nombre: initialData.paciente_nombre,
        latitud: initialData.latitud,
        longitud: initialData.longitud,
        proyecto: initialData.proyecto,
        codigo_proyecto_investigacion: initialData.codigo_proyecto_investigacion || initialData.paciente_codigo_proyecto_investigacion
    } : selectedPaciente;

    const [parasitos, setParasitos] = useState({
        "Ascaris": { enabled: false, eggs: 0, hpg: 0, intensity: "Negativo", result: "NEGATIVO" },
        "Trichuris": { enabled: false, eggs: 0, hpg: 0, intensity: "Negativo", result: "NEGATIVO" },
        "Uncinarias": { enabled: false, eggs: 0, hpg: 0, intensity: "Negativo", result: "NEGATIVO" }
    });
    const [observaciones, setObservaciones] = useState("");

    const calculateIntensity = (name, hpg) => {
        if (hpg === 0) return "Negativo";
        if (name === "Ascaris") {
            if (hpg >= 1 && hpg <= 4999) return "Leve";
            if (hpg >= 5000 && hpg <= 49999) return "Moderada";
            if (hpg >= 50000) return "Severa";
        }
        if (name === "Trichuris") {
            if (hpg >= 1 && hpg <= 999) return "Leve";
            if (hpg >= 1000 && hpg <= 9999) return "Moderada";
            if (hpg >= 10000) return "Severa";
        }
        if (name === "Uncinarias") {
            if (hpg >= 1 && hpg <= 1999) return "Leve";
            if (hpg >= 2000 && hpg <= 3999) return "Moderada";
            if (hpg >= 4000) return "Severa";
        }
        return "Negativo";
    };

    const handleEggChange = (name, val) => {
        const eggs = parseInt(val) || 0;
        const hpg = eggs * 24;
        const intensity = calculateIntensity(name, hpg);
        setParasitos(prev => ({
            ...prev,
            [name]: { ...prev[name], eggs, hpg, intensity, enabled: eggs > 0, result: eggs > 0 ? "POSITIVO" : prev[name].result }
        }));
    };

    const handleResultChange = (name, res) => {
        setParasitos(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                result: res,
                enabled: res === "POSITIVO",
                eggs: res === "NEGATIVO" ? 0 : prev[name].eggs,
                hpg: res === "NEGATIVO" ? 0 : prev[name].hpg,
                intensity: res === "NEGATIVO" ? "Negativo" : prev[name].intensity
            }
        }));
    };

    useEffect(() => {
        if (isEditMode && initialData) {
            const intensityMap = initialData.intensidad_hpg || {};
            const pList = initialData.parasitos_detectados || [];
            const newParasitos = { ...parasitos };
            ["Ascaris", "Trichuris", "Uncinarias"].forEach(name => {
                const hpg = intensityMap[name] || 0;
                const isPos = pList.includes(name);
                newParasitos[name] = {
                    enabled: isPos,
                    result: isPos ? "POSITIVO" : "NEGATIVO",
                    eggs: Math.floor(hpg / 24),
                    hpg,
                    intensity: calculateIntensity(name, hpg)
                };
            });
            setParasitos(newParasitos);
            setObservaciones(initialData.observaciones || "");
        }
    }, [initialData, isEditMode]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const detected = Object.keys(parasitos).filter(k => parasitos[k].enabled);
        const intensityMap = {};
        detected.forEach(k => { intensityMap[k] = parasitos[k].hpg; });

        const payload = {
            paciente: effectivePaciente.id,
            latitud: effectivePaciente.latitud || 8.75,
            longitud: effectivePaciente.longitud || -75.88,
            fecha_registro: initialData?.fecha_registro || new Date().toLocaleDateString('en-CA'),
            observaciones,
            nivel_peligrosidad: detected.length >= 2 ? "ALTO" : (detected.length === 1 ? "MEDIO" : "BAJO"),
            parasitos_detectados: detected,
            intensidad_hpg: intensityMap,
            proyecto: effectivePaciente.proyecto || activeProjectId,
            codigo_proyecto_investigacion: effectivePaciente.codigo_proyecto_investigacion || ""
        };

        try {
            if (isEditMode && initialData.id) {
                await axios.put(`${Apiurl}casos/${initialData.id}/`, payload, getAuthHeader());
                showToast("✅ Caso actualizado correctamente.", "success");
            } else {
                await axios.post(`${Apiurl}casos/`, payload, getAuthHeader());
                showToast("✅ Caso de investigación guardado.", "success");
            }
            setSelectedPaciente(null);
            if (onCancel) onCancel();
            else setView("misCasos");
        } catch (err) {
            showToast("Error guardando el caso.", "danger");
        }
    };

    return { effectivePaciente, parasitos, observaciones, setObservaciones, handleEggChange, handleResultChange, handleSubmit };
};
